import React, {useRef, useEffect, useState} from 'react'
import {useParams, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Grid,
    GridItem,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {usePage} from '@salesforce/commerce-sdk-react'
import {PDCAboutStoryBlockExcerpt} from '../../layouts'
import {SimpleMagazineBlogPost} from '../../layouts'
import {MAGAZINE_NB_ARTICLES_PER_PAGE, MAGAZINE_NB_ARTICLES_INITIAL} from '../../../constants.js'

// React helmet
import {Helmet} from 'react-helmet'

// Utils
import {getEnvStaleTime} from '../../../utils/utils.js'

// Custom Utils
import {getAppDomain} from '../../../utils/utils'

/**
 * PDCBlogArticlesList is a React component that displays a paginated list of blog articles.
 * It fetches and manages the display of articles based on the current page and allows users
 * to load more articles dynamically.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.custom - Custom properties passed to the component.
 *
 * @returns {JSX.Element} A React component that renders a list of blog articles with pagination controls.
 *
 * The component utilizes several hooks and utilities:
 * - `useIntl`: For internationalization and formatting messages.
 * - `useParams`: To retrieve the current page ID from the URL parameters.
 * - `usePage`: To fetch page data including articles using the page ID.
 * - `useLocation`: To access the current URL location.
 * - `useAppOrigin`: To get the application's origin URL.
 * - `useState`: To manage the state of visible articles and pagination.
 * - `useEffect`: To handle side effects, such as focusing on newly loaded articles.
 * - `useRef`: To reference the newly loaded article for focus management.
 *
 * The component calculates pagination details such as the total number of pages,
 * the current page, and the range of articles to display. It provides "Load more"
 * functionality to append more articles to the list.
 *
 * The component also manages navigation links for previous and next pages using
 * the `Helmet` component to update the document head with pagination links.
 *
 * @example
 * <PDCBlogArticlesList custom={customData} />
 */
export const PDCBlogArticlesList = ({custom}) => {
    const intl = useIntl()
    const {pageId} = useParams()
    const staleTime = getEnvStaleTime()
    const {data: page, error} = usePage({parameters: {pageId}}, {staleTime: staleTime})
    const location = useLocation()
    const appOrigin = getAppDomain()
    const searchParams = new URLSearchParams(location.search)
    const pageParam = searchParams.get('p')

    const articlePages = page?.custom?.articlePages || []
    const totalPages = Math.ceil(articlePages.length / MAGAZINE_NB_ARTICLES_PER_PAGE)
    const currentPage = pageParam ? Math.min(parseInt(pageParam), totalPages) : 0

    const start =
        currentPage === 0
            ? 0
            : (currentPage - 1) * MAGAZINE_NB_ARTICLES_PER_PAGE + MAGAZINE_NB_ARTICLES_INITIAL
    const end =
        currentPage === 0
            ? MAGAZINE_NB_ARTICLES_INITIAL
            : currentPage * MAGAZINE_NB_ARTICLES_PER_PAGE
    const [pagesToDisplay, setPagesToDisplay] = useState(articlePages.slice(start, end))

    const [visibleItemCount, setVisibleItemCount] = useState(
        currentPage === 0
            ? MAGAZINE_NB_ARTICLES_INITIAL
            : (currentPage + 1) * MAGAZINE_NB_ARTICLES_PER_PAGE
    )

    const [previouslyLoadedItemCount, setPreviouslyLoadedItemCount] = useState(-1)

    /**
     * Handles the loading of additional articles in the list by updating the visible item count.
     *
     * This function calculates the number of articles to load based on the remaining articles
     * and a predefined constant `MAGAZINE_NB_ARTICLES_PER_PAGE`. It updates the state to reflect
     * the new number of articles to display and keeps track of the previously loaded item count.
     *
     * @function handleLoadMore
     * @returns {void}
     */
    const handleLoadMore = () => {
        setVisibleItemCount((prevCount) => {
            setPreviouslyLoadedItemCount(prevCount)
            const remainingArticles = articlePages.length - prevCount
            const articlesToLoad = Math.min(MAGAZINE_NB_ARTICLES_PER_PAGE, remainingArticles)

            setPagesToDisplay(articlePages.slice(0, prevCount + articlesToLoad))
            return prevCount + articlesToLoad
        })
    }

    const newArticleRef = useRef(null)

    useEffect(() => {
        // Focus on the newly added article
        if (newArticleRef.current) {
            newArticleRef.current.querySelector('a')?.focus({focusVisible: true})
        }
    }, [pagesToDisplay])

    const prevPageUrl =
        pageParam && parseInt(pageParam) > 0
            ? `${appOrigin}${location.pathname}?p=${parseInt(pageParam) - 1}`
            : null

    const nextPageUrl =
        visibleItemCount < articlePages.length
            ? `${appOrigin}${location.pathname}?p=${parseInt(pageParam || 0) + 1}`
            : null

    return (
        !error && (
            <>
                <StaticPaginationLinks
                    articlePages={articlePages}
                    appOrigin={appOrigin}
                    location={location}
                />
                <Box textAlign={'center'}>
                    <Helmet>
                        {prevPageUrl && <link rel="prev" href={prevPageUrl} />}
                        {nextPageUrl && <link rel="next" href={nextPageUrl} />}
                    </Helmet>
                    <Grid
                        as="ul"
                        templateColumns={[
                            'repeat(1, 1fr)',
                            null,
                            'repeat(2, 1fr)',
                            'repeat(3, 1fr)'
                        ]}
                        listStyleType={'none'}
                    >
                        {pagesToDisplay?.map((page, i) => (
                            <GridItem
                                as="li"
                                colSpan={i % 7 === 0 ? [1, null, 2, 3] : 1}
                                key={`article-${i}`}
                                ref={i === previouslyLoadedItemCount ? newArticleRef : null}
                            >
                                {i % 7 === 0 ? (
                                    <PDCAboutStoryBlockExcerpt page={page} />
                                ) : (
                                    <SimpleMagazineBlogPost
                                        p={['20px 15px', null, '34px 40px', '64px 77px']}
                                        page={page}
                                    />
                                )}
                            </GridItem>
                        ))}
                    </Grid>
                    {visibleItemCount < articlePages.length && (
                        <Button
                            variant="secondary"
                            onClick={handleLoadMore}
                            m={['30px 0 54px', null, '44px 0 94px']}
                        >
                            {intl.formatMessage(
                                {
                                    id: 'magazine.pagination.load_more',
                                    defaultMessage: 'Load more + {nbOfArticlesPerPage}'
                                },
                                {
                                    nbOfArticlesPerPage: Math.min(
                                        MAGAZINE_NB_ARTICLES_PER_PAGE,
                                        articlePages.length - visibleItemCount
                                    )
                                }
                            )}
                        </Button>
                    )}
                </Box>
            </>
        )
    )
}

PDCBlogArticlesList.propTypes = {
    custom: PropTypes.object
}

/**
 * StaticPaginationLinks is a React functional component that generates a list of pagination links
 * for navigating through a series of article pages. It calculates the total number of pages based
 * on the number of articles and a predefined number of articles per page, then creates a set of
 * links for each page.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Array} props.articlePages - An array representing the pages of articles. This array is
 * required and is used to determine the total number of pagination links.
 * @param {string} props.appOrigin - A string representing the origin of the application. This is
 * required and is used to construct the URL for each pagination link.
 * @param {Object} props.location - An object representing the current location. This is required
 * and is used to extract the pathname for constructing the URL for each pagination link.
 *
 * @returns {JSX.Element} A React element containing a set of pagination links, each wrapped in a
 * `Link` component. The links are hidden within a `Box` component with `display` set to "none".
 */
const StaticPaginationLinks = ({articlePages, appOrigin, location}) => {
    const totalPages = Math.ceil(articlePages.length / MAGAZINE_NB_ARTICLES_PER_PAGE)

    const links = Array.from({length: totalPages}, (_, index) => {
        const pageParam = index === 0 ? '' : `?p=${index}`
        const url = `${appOrigin}${location.pathname}${pageParam}`
        return (
            <Link key={index} href={url} display="block">
                {url}
            </Link>
        )
    })

    return <Box display="none">{links}</Box>
}

StaticPaginationLinks.propTypes = {
    articlePages: PropTypes.array.isRequired,
    appOrigin: PropTypes.string.isRequired,
    location: PropTypes.object.isRequired
}
